/**
 * Login. The provider buttons are plain <a> elements on purpose: they hit API
 * routes that 302 to the provider, so a full navigation is required — client
 * routing can't follow an external redirect. Styled to match the "/" landing
 * page (see ../landing.css for why Tailwind is scoped + "tw:"-prefixed here,
 * and Home.tsx for the color rationale — --twitch/--youtube for the provider
 * buttons, the --brand token for accents, `dark:` wired to the same
 * data-theme attribute useTheme() sets).
 */
import { Link } from "react-router-dom";
import { motion, type Variants } from "framer-motion";
import { useTheme } from "../hooks/useTheme";
import { Wordmark } from "../components/Wordmark";
import { ThemeToggle } from "../components/ThemeToggle";
import { apiUrl } from "../lib/api";
import "../landing.css";

const fadeInUp: Variants = {
  hidden: { y: 40, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export function Login() {
  const [theme, toggleTheme] = useTheme();

  return (
    <div className="tw:bg-white tw:dark:bg-[#0f1115] tw:text-neutral-800 tw:dark:text-[#e6e8ee] tw:font-sans tw:min-h-screen tw:flex tw:items-center tw:justify-center tw:p-6 tw:relative tw:overflow-hidden">
      <div className="tw:fixed tw:top-6 tw:inset-x-6 tw:z-50 tw:flex tw:justify-between tw:items-center">
        <Link
          to="/"
          className="tw:no-underline tw:bg-white tw:dark:bg-[#171a21] tw:border tw:border-transparent tw:dark:border-[#2a2f3a] tw:pl-4 tw:pr-5 tw:py-2 tw:rounded-full tw:shadow-md"
        >
          <Wordmark size="2.25rem" />
        </Link>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
      </div>

      <div className="tw:absolute tw:top-[-10%] tw:right-[-10%] tw:w-96 tw:h-96 tw:bg-[#e06d10]/10 tw:dark:bg-[#fb923c]/10 tw:rounded-full tw:blur-3xl" />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
        className="tw:bg-white tw:dark:bg-[#1e222b] tw:p-12 tw:md:p-16 tw:rounded-3xl tw:shadow-xl tw:max-w-md tw:w-full tw:border tw:border-neutral-100 tw:dark:border-[#2a2f3a] tw:relative tw:z-10 tw:text-center"
      >
        <span className="tw:text-[#e06d10] tw:dark:text-[#fb923c] tw:font-bold tw:tracking-widest tw:uppercase tw:text-sm tw:mb-4 tw:block">
          Welcome back
        </span>
        <h1 className="tw:text-4xl tw:font-medium tw:tracking-tight tw:leading-tight tw:mb-4">
          Let&rsquo;s clear the queue.
        </h1>
        <p className="tw:text-neutral-500 tw:dark:text-[#949aa8] tw:leading-relaxed tw:mb-10">
          Streamer &amp; vouched-in mods only. Streamers: the platform you
          broadcast from. Mods: whichever platform your invite went to.
        </p>

        <div className="tw:flex tw:flex-col tw:gap-4">
          <a
            href={apiUrl("/auth/twitch")}
            className="tw:no-underline tw:bg-[#9146ff] tw:text-white tw:px-10 tw:py-4 tw:rounded-full tw:font-bold tw:shadow-md tw:hover:brightness-90 tw:transition-[filter]"
          >
            Continue with Twitch
          </a>
          <a
            href={apiUrl("/auth/youtube")}
            className="tw:no-underline tw:bg-[#ff0033] tw:text-white tw:px-10 tw:py-4 tw:rounded-full tw:font-bold tw:shadow-md tw:hover:brightness-90 tw:transition-[filter]"
          >
            Continue with YouTube
          </a>
          <a
            href={apiUrl("/auth/discord")}
            className="tw:no-underline tw:bg-[#5865f2] tw:text-white tw:px-10 tw:py-4 tw:rounded-full tw:font-bold tw:shadow-md tw:hover:brightness-90 tw:transition-[filter]"
          >
            Continue with Discord
          </a>
        </div>

        <p className="tw:text-xs tw:text-neutral-400 tw:dark:text-[#6b7280] tw:mt-6">
          By continuing you agree to quelby&rsquo;s{" "}
          <Link to="/terms" className="tw:text-[#e06d10] tw:dark:text-[#fb923c] tw:hover:underline">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="tw:text-[#e06d10] tw:dark:text-[#fb923c] tw:hover:underline">
            Privacy Policy
          </Link>
          .
        </p>

        {/* Local-only shortcut. Rendered only by the Vite dev server, and the
            endpoint it hits is itself hard-gated to the local stage. */}
        {import.meta.env.DEV && (
          <a
            href={apiUrl("/auth/dev-login?platform=twitch&id=dev-local&name=Dev+Admin")}
            className="tw:no-underline tw:block tw:mt-6 tw:text-sm tw:text-neutral-400 tw:dark:text-[#6b7280] tw:hover:text-neutral-600 tw:dark:hover:text-[#949aa8]"
          >
            Dev login (local only)
          </a>
        )}
      </motion.div>
    </div>
  );
}
